import { useState } from 'react';
import { sendEmailSignInLink } from '../../auth/firebase';
import '../../styles.css';

interface GuestSignInProps {
  onBack: () => void;
}

type SignInState = 'initial' | 'sending' | 'sent' | 'error';

const GuestSignIn = ({ onBack }: GuestSignInProps) => {
  const [email, setEmail] = useState('');
  const [state, setState] = useState<SignInState>('initial');
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !email.includes('@')) {
      setError('Please enter a valid email address.');
      return;
    }

    setState('sending');
    setError(null);

    try {
      await sendEmailSignInLink(email);
      setState('sent');
    } catch (err: any) {
      setState('error');
      setError(err.message || "We couldn't send the sign-in link to that email. Please check the email and try again.");
    }
  };

  if (state === 'sent') {
    return (
      <div className="guest-signin-container">
        <div className="guest-signin-card">
          <div className="guest-signin-success">
            <div className="success-icon">✉️</div>
            <h2>Check Your Email</h2>
            <p>
              We've sent a sign-in link to <strong>{email}</strong>
            </p>
            <p className="hint">
              Click the link in the email to continue. The link expires in 1 hour.
            </p>
            <p className="hint" style={{ fontSize: '0.85rem', color: '#666' }}>
              If you're a returning user, you'll be signed in directly.
              <br />
              New users will need admin approval before accessing the dashboard.
            </p>
            <p className="hint">
              Don't see it? Check your spam folder.
            </p>
            <button 
              className="wizrdom-button secondary"
              onClick={() => {
                setState('initial');
                setEmail('');
              }}
            >
              Try a different email
            </button>
          </div>
        </div>
        <button className="back-link" onClick={onBack}>
          ← Back to sign-in options
        </button>
      </div>
    );
  }

  return (
    <div className="guest-signin-container">
      <div className="guest-signin-card">
        <h2>Guest / Partner Access</h2>
        <p className="description">
          Sign in or create a new account using your email address.
          <br />
          <strong>No password required</strong> - we'll send you a secure link.
        </p>
        
        <form onSubmit={handleSubmit} className="guest-signin-form">
          <div className="form-group">
            <label htmlFor="email">Email Address</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your.email@example.com"
              disabled={state === 'sending'}
              autoFocus
            />
          </div>

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <button 
            type="submit" 
            className="wizrdom-button primary"
            disabled={state === 'sending'}
          >
            {state === 'sending' ? 'Sending...' : 'Continue with Email'}
          </button>
        </form>

        <div className="info-text">
          <p><strong>Returning user?</strong> Enter your registered email to sign back in.</p>
          <p><strong>New user?</strong> Your account will be created and await admin approval.</p>
        </div>
      </div>
      
      <button className="back-link" onClick={onBack}>
        ← Back to sign-in options
      </button>
    </div>
  );
};

export default GuestSignIn;
